/**
 * KisiDosyaLink.jsx — Bir kişi belgesini / vesikalığını kimlik doğrulamalı uçtan açar.
 *
 * Servis: services/personelRosterService.js (`fetchKisiDosya`, `fetchKisiFotograf`)
 * Rehber: docs/guides/FE-PersonnelController-guide.md §7 · FE-local-file-storage-guide.md
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * NEDEN AYRI BİR BİLEŞEN: kişi dosyaları `/public/files/...` ile SUNULMUYOR ve
 * sunulmayacak. Bir SGK işe giriş bildirgesi TC kimlik numarası taşır, `file_type` 90
 * kimlik belgesinin fotoğrafıdır — KVKK'ya tabi veriye "tahmin edilemez dosya adı"
 * denetimiyle açık URL verilmiyor. Dolayısıyla `<img src>` / `<a href>` kurulamaz;
 * baytlar oturumla birlikte blob olarak çekilir.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * ÜÇ DURUM VAR VE ÜÇÜ DE FARKLI ŞEY SÖYLER:
 *   1. `fileId` null  → belge yüklenmemiş. (Taşeronun kadrosunu okuyan dekoratörün "belge VAR, tutamağı
 *      yok" hali ve onun `delege` prop'u #381/#382 ile kalktı: başkasının kadrosu artık okunmuyor.)
 *   2. LEGACY 404 → satır GERÇEK, baytlar eski PHP `repo/` ağacında. ⚠️ BUGÜN NEREDEYSE
 *      HER ÇAĞRI BURAYA DÜŞER (73.633 bağın 73.633'ü, 31.638 vesikalığın 31.638'i), bu
 *      yüzden VARSAYILAN GÖRÜNÜM budur — hata bildirimi değil. Backend'in Türkçe mesajı
 *      olduğu gibi gösterilir; "belge bulunamadı" demek çok daha ürkütücü ve YANLIŞ.
 *   3. 403 → çağıran bu kişiyi hiç işe almamış (dosya kapısı sahiplik kapsamını okur).
 */
import React, { useState } from "react";
import { Button, Spinner, Tooltip } from "reactstrap";
import { FileText, Image as ImageIcon, Lock, Archive } from "lucide-react";
import { fetchKisiDosya, fetchKisiFotograf } from "../../services/personelRosterService";
import { useTranslation } from "react-i18next";

export default function KisiDosyaLink({
  personId,
  fileId = null,
  fotograf = false,
  label,
  size = "sm",
  color = "light",
  className = "",
}) {
  const { t } = useTranslation("components");
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState(null); // { legacy, text }
  const [tooltip, setTooltip] = useState(false);

  const id = `kisi-dosya-${personId}-${fotograf ? "foto" : fileId ?? "yok"}`;
  const yok = !fotograf && !fileId;

  async function open() {
    setLoading(true);
    setNote(null);
    try {
      const url = fotograf ? await fetchKisiFotograf(personId) : await fetchKisiDosya(personId, fileId);
      // Yeni sekmede aç: `Content-Disposition: inline` ve X-Frame-Options'a takılmayan
      // tek yol bu (iframe project_files'ta da bloklanıyor, bkz. local-backend-testing.md).
      window.open(url, "_blank", "noopener");
      // Blob URL'i sekme aldıktan sonra bırak — hemen revoke edersek sekme boş açılır.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setNote({ legacy: !!e.legacy, text: e.message });
    } finally {
      setLoading(false);
    }
  }

  if (yok) {
    return (
      <span className={`small text-muted d-inline-flex align-items-center gap-1 ${className}`}>
        <Lock size={13} />
        {t("kisiDosya.dosyaYok")}
      </span>
    );
  }

  return (
    <span className={className}>
      <Button id={id} size={size} color={color} onClick={open} disabled={loading}>
        {loading ? <Spinner size="sm" /> : fotograf ? <ImageIcon size={13} /> : <FileText size={13} />}
        {label ? <span className="ms-1">{label}</span> : null}
      </Button>
      {note && (
        <span className={`small ms-2 ${note.legacy ? "text-muted" : "text-danger"}`}>
          {note.legacy && <Archive size={13} className="me-1" />}
          {note.text}
        </span>
      )}
      <Tooltip target={id} isOpen={tooltip} toggle={() => setTooltip(!tooltip)}>
        {t("kisiDosya.belgeOturumunuzlaBirlikteIndirilirPaylasilabilir")}
      </Tooltip>
    </span>
  );
}
